import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Pencil } from 'lucide-react';
import { format } from 'date-fns';

export default function Settings() {
  const [settings, setSettings] = useState([]);
  const [feeSettings, setFeeSettings] = useState([]);
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const ownerDefault = { property_id: '', owner_name: '', owner_email: '', ownership_percentage: 100, management_fee_percentage: 0 };
  const [ownerForm, setOwnerForm] = useState(ownerDefault);

  useEffect(() => {
    Promise.all([
      api.get('/system-settings'), api.get('/client-fee-settings'), api.get('/property-owners'), api.get('/properties')
    ]).then(([s, f, o, p]) => {
      setSettings(s.data); setFeeSettings(f.data); setOwners(o.data); setProperties(p.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleOwnerSubmit = async (e) => {
    e.preventDefault();
    if (editingOwner) await api.put(`/property-owners/${editingOwner.id}`, ownerForm);
    else await api.post('/property-owners', ownerForm);
    setOwnerDialogOpen(false); setEditingOwner(null); setOwnerForm(ownerDefault);
    const { data } = await api.get('/property-owners'); setOwners(data);
  };

  const editOwner = (o) => {
    setEditingOwner(o); setOwnerForm({ property_id: o.property_id, owner_name: o.owner_name, owner_email: o.owner_email, ownership_percentage: o.ownership_percentage || 100, management_fee_percentage: o.management_fee_percentage || 0 }); setOwnerDialogOpen(true);
  };

  const deleteOwner = async (id) => { if (!window.confirm('Delete owner?')) return; await api.delete(`/property-owners/${id}`); const { data } = await api.get('/property-owners'); setOwners(data); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="settings-page">
      <h1 className="page-title mb-6">Settings</h1>

      <Tabs defaultValue="owners" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="owners">Property Owners</TabsTrigger>
          <TabsTrigger value="fees">Fee Settings</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
        </TabsList>

        <TabsContent value="owners">
          <div className="page-header">
            <h2 className="text-lg font-semibold font-heading">Property Owners</h2>
            <Dialog open={ownerDialogOpen} onOpenChange={(o) => { setOwnerDialogOpen(o); if (!o) { setEditingOwner(null); setOwnerForm(ownerDefault); } }}>
              <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-owner-btn"><Plus className="w-4 h-4 mr-2" />Add Owner</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">{editingOwner ? 'Edit Owner' : 'Add Owner'}</DialogTitle></DialogHeader>
                <form onSubmit={handleOwnerSubmit} className="space-y-4">
                  <div><Label>Property</Label>
                    <Select value={ownerForm.property_id} onValueChange={v => setOwnerForm({ ...ownerForm, property_id: v })}>
                      <SelectTrigger data-testid="owner-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                      <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Owner Name</Label><Input value={ownerForm.owner_name} onChange={e => setOwnerForm({ ...ownerForm, owner_name: e.target.value })} required data-testid="owner-name-input" /></div>
                  <div><Label>Owner Email</Label><Input type="email" value={ownerForm.owner_email} onChange={e => setOwnerForm({ ...ownerForm, owner_email: e.target.value })} required data-testid="owner-email-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Ownership %</Label><Input type="number" value={ownerForm.ownership_percentage} onChange={e => setOwnerForm({ ...ownerForm, ownership_percentage: Number(e.target.value) })} data-testid="owner-percentage-input" /></div>
                    <div><Label>Mgmt Fee %</Label><Input type="number" value={ownerForm.management_fee_percentage} onChange={e => setOwnerForm({ ...ownerForm, management_fee_percentage: Number(e.target.value) })} data-testid="owner-mgmt-fee-input" /></div>
                  </div>
                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="owner-submit-btn">{editingOwner ? 'Update' : 'Create'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="table-wrap">
            <table className="w-full data-table min-w-[600px]">
              <thead><tr><th>Owner</th><th>Email</th><th>Property</th><th>Ownership %</th><th>Mgmt Fee %</th><th>Actions</th></tr></thead>
              <tbody>
                {owners.map(o => (
                  <tr key={o.id} data-testid={`owner-row-${o.id}`}>
                    <td className="font-medium text-slate-900">{o.owner_name}</td>
                    <td>{o.owner_email}</td>
                    <td>{o.property_name}</td>
                    <td>{o.ownership_percentage}%</td>
                    <td>{o.management_fee_percentage}%</td>
                    <td>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => editOwner(o)} data-testid={`edit-owner-${o.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {owners.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No owners</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="fees">
          <h2 className="text-lg font-semibold font-heading mb-4">Fee Configurations</h2>
          {feeSettings.length > 0 ? (
            <div className="space-y-4">
              {feeSettings.map(fs => (
                <div key={fs.id} className="bg-white border border-slate-200 rounded-lg p-6" data-testid={`fee-setting-${fs.id}`}>
                  <h3 className="font-medium text-slate-900 mb-3">Owner: {fs.owner_id?.slice(-6)}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div><span className="text-slate-500">Booking Fee:</span> <span className="font-medium">{fs.booking_fee} ({fs.booking_fee_type})</span></div>
                    <div><span className="text-slate-500">Cleaning Fee:</span> <span className="font-medium">{fs.cleaning_fee} ({fs.cleaning_fee_type})</span></div>
                    <div><span className="text-slate-500">Marketing:</span> <span className="font-medium">{fs.marketing_fee}</span></div>
                    <div><span className="text-slate-500">VAT:</span> <span className="font-medium">{fs.include_vat ? `${fs.vat_percentage}%` : 'No'}</span></div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">No fee settings configured</p>}
        </TabsContent>

        <TabsContent value="system">
          <h2 className="text-lg font-semibold font-heading mb-4">System Settings</h2>
          {settings.length > 0 ? (
            <div className="space-y-4">
              {settings.map(s => (
                <div key={s.id || s.key} className="bg-white border border-slate-200 rounded-lg p-6">
                  <h3 className="font-medium text-slate-900 mb-2">{s.key}</h3>
                  {s.last_run && <p className="text-sm text-slate-500">Last run: {format(new Date(s.last_run), 'MMM dd, yyyy HH:mm')}</p>}
                  {s.sources_synced !== undefined && <p className="text-sm text-slate-500">Sources synced: {s.sources_synced} | Failed: {s.sources_failed || 0}</p>}
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">No system settings</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
